import { Dispatch } from "redux";
import {
  FETCH_ORGANIZATION_SETTINGS_FAILURE,
  FETCH_ORGANIZATION_SETTINGS_REQUEST,
  FETCH_ORGANIZATION_SETTINGS_SUCCESS,
  FetchOrganizationSettingsSuccessAction,
  FetchOrganizationSettingsFailureAction,
  FetchOrganizationSettingsRequestAction,
  SET_ORGANIZATION_SETTINGS_REQUEST,
  SET_ORGANIZATION_SETTINGS_SUCCESS,
  SET_ORGANIZATION_SETTINGS_FAILURE,
  SetOrganizationSettingsRequestAction,
  SetOrganizationSettingsSuccessAction,
  SetOrganizationSettingsFailureAction,
  SetLocalDiscountsAction,
  SET_LOCAL_DISCOUNTS,
  OrganizationSettingsActionTypes,
} from "./types";
import {
  OrganizationSettingsData,
  OrganizationSettingsUpdateData,
} from "../../models/organizationSettingsData";
import { enqueueSnackbar } from "notistack";
import { organizationSettingsService } from "../../api";
import { getDynamicTranslation } from "../../components/core/localization/localizationUtils";
import { OrganizationDiscount } from "../../models/organizationSettingsData";

export const fetchOrganizationSettingsRequest =
  (): FetchOrganizationSettingsRequestAction => {
    return {
      type: FETCH_ORGANIZATION_SETTINGS_REQUEST,
    };
  };

export const fetchOrganizationSettingsSuccess = (
  data: OrganizationSettingsData
): FetchOrganizationSettingsSuccessAction => {
  return {
    type: FETCH_ORGANIZATION_SETTINGS_SUCCESS,
    data,
  };
};

export const fetchOrganizationSettingsFailure = (
  error: string
): FetchOrganizationSettingsFailureAction => {
  return {
    type: FETCH_ORGANIZATION_SETTINGS_FAILURE,
    error,
  };
};

export const setOrganizationSettingsRequestAction =
  (): SetOrganizationSettingsRequestAction => {
    return {
      type: SET_ORGANIZATION_SETTINGS_REQUEST,
    };
  };

export const setOrganizationSettingsSuccess = (
  data: OrganizationSettingsData
): SetOrganizationSettingsSuccessAction => {
  return {
    type: SET_ORGANIZATION_SETTINGS_SUCCESS,
    data,
  };
};

export const setOrganizationSettingsFailure = (
  error: string
): SetOrganizationSettingsFailureAction => {
  return {
    type: SET_ORGANIZATION_SETTINGS_FAILURE,
    error,
  };
};

export const setLocalDiscounts = (
  discounts: OrganizationDiscount[]
): SetLocalDiscountsAction => {
  return {
    type: SET_LOCAL_DISCOUNTS,
    discounts,
  };
};

/**
 * Action creator to fetch the organization settings from the API.
 * @param {string} organizationId - The ID of the organization to fetch settings for.
 * @returns {Function} Thunk action.
 */
export const fetchOrganizationSettings = (organizationId: string) => {
  return async (dispatch: Dispatch<OrganizationSettingsActionTypes>) => {
    dispatch(fetchOrganizationSettingsRequest());
    try {
      const response =
        await organizationSettingsService.getOrganizationSettings(organizationId);
      dispatch(fetchOrganizationSettingsSuccess(response));
    } catch (error) {
      const errorMessage = getDynamicTranslation(
        "organization.loadOrgaSettingsError",
        "snackbars"
      );
      dispatch(fetchOrganizationSettingsFailure(errorMessage));
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };
};

/**
 * Action creator to update the organization settings.
 * @param {string} organizationSettingsId - The ID of the organization setting to be updated.
 * @param {OrganizationSettingsUpdateData} data - The updated settings data.
 * @returns {Function} Thunk action.
 */
export const setOrganizationSettings = (
  organizationSettingsId: string,
  data: OrganizationSettingsUpdateData
) => {
  return async (dispatch: Dispatch<OrganizationSettingsActionTypes>) => {
    dispatch(setOrganizationSettingsRequestAction());
    try {
      const response = await organizationSettingsService.setOrganizationSettings(
        organizationSettingsId,
        data
      );
      dispatch(setOrganizationSettingsSuccess(response));
      enqueueSnackbar(
        getDynamicTranslation("organization.orgaSettingsSuccess", "snackbars"),
        {
          variant: "success",
        }
      );
    } catch (error) {
      const errorMessage = getDynamicTranslation(
        "organization.orgaSettingsError",
        "snackbars"
      );
      dispatch(setOrganizationSettingsFailure(errorMessage));
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };
};
